import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

import { request } from '../../shared/request';
import { ENV } from '../../shared/constants';
import { withResponseHandling, getErrorMessage } from '../../shared/responseHandler';
import Loading from '../../components/Loading/Loading';
import PopupInfo from '../../components/PopupInfo/PopupInfo';
import { formatEventPassOffers, GA } from '../../shared/helpers';
import { initializeOffersFromApi } from '../../shared/offersHelpers';

/**
 * CheckoutInit Component
 * 
 * This component handles checkout initialization when users arrive with URL parameters
 * (offerId, offerType, playerId, source). It manages different scenarios:
 * 
 * 1. User not logged in - authenticate with provided playerId
 * 2. User logged in with different ID - re-authenticate with new playerId
 * 3. User logged in with same ID - proceed to checkout
 * 
 * URL Parameters:
 * - oid: Offer ID
 * - otype: Offer Type
 * - pid: Player ID
 * - source: Source type (e.g., game, site)
 */
const CheckoutInit = ({ setPlayer, setIsLogged, setOffers, config, setIsMarketingEmailGiven }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isFirstPageView = useRef(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState({});

    const offerId = searchParams.get('oid');
    const offerType = searchParams.get('otype');
    const playerId = searchParams.get('pid');
    const source = searchParams.get('source');

    /**
     * Validates that all required URL parameters are present
     */
    const validateParams = () => {
        if (!offerId || !offerType || !playerId) {
            toast.error(config.errors.WENT_WRONG);
            navigate('/');
            return false;
        }
        return true;
    };

    /**
     * Authenticates player and fetches shop data
     */
    const authenticatePlayer = async (playerIdToAuth) => {
        try {
            setIsLoading(true);
            const idNoSymbols = playerIdToAuth.replace(/[^A-Za-z0-9]+/g, '');
            
            const result = await withResponseHandling(
                () => request.get(`getWebShopInfo/${idNoSymbols}`),
                {
                    navigate,
                    onSuccess: (response) => {
                        const playerInfo = {
                            name: response.PlayerName,
                            id: idNoSymbols,
                            currency: response.Currency,
                        };
                        
                        localStorage.setItem('playerInfo', JSON.stringify(playerInfo));
                        setPlayer(playerInfo);
                        setIsLogged(true);
                        setIsMarketingEmailGiven(response.IsMarketingEmailGiven);
                        
                        // Use helper to initialize offers from API response
                        const offersData = initializeOffersFromApi(response, formatEventPassOffers);
                        setOffers(offersData);

                        // Track user authentication for analytics (only on live environment)
                        if (isFirstPageView.current && ENV === 'live') {
                            const userType = source ? 'gameToWebShop' : 'directWebShop';
                            GA.setUserData(idNoSymbols);
                            GA.setUserDataProperties(idNoSymbols, userType);
                            GA.isUserTrackedAsLogged = true;
                            isFirstPageView.current = false;
                        }
                    },
                    onAccessDenied: (reason) => {
                        toast.error(getErrorMessage(reason, config));
                        navigate('/');
                    },
                    onError: (err) => {
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null,
                        });
                    },
                }
            );

            return result.status === 'SUCCESS' ? idNoSymbols : null;
        } catch (err) {
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null,
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Redirects to checkout page with proper parameters
     */
    const proceedToCheckout = (authenticatedPlayerId) => {
        const checkoutUrl = `/checkout?oid=${offerId}&otype=${offerType}&pid=${authenticatedPlayerId}&source=${source || ''}`;
        navigate(checkoutUrl);
    };

    /**
     * Main initialization logic
     */
    const initializeCheckout = async () => {
        // Validate URL parameters
        if (!validateParams()) {
            return;
        }

        // Get current player from localStorage
        let currentPlayer = localStorage.getItem('playerInfo');
        currentPlayer = JSON.parse(currentPlayer);

        const currentPlayerId = currentPlayer?.id;
        const incomingPlayerId = playerId.replace(/[^A-Za-z0-9]+/g, '');

        // Scenario 1: No user logged in OR different user in URL
        if (!currentPlayerId || currentPlayerId !== incomingPlayerId) {
            const authenticatedPlayerId = await authenticatePlayer(playerId);
            if (authenticatedPlayerId) {
                proceedToCheckout(authenticatedPlayerId);
            }
        } 
        // Scenario 2: Same user already logged in
        else {
            proceedToCheckout(currentPlayerId);
        }
    };

    useEffect(() => {
        initializeCheckout();
    }, []);

    return isLoading ? (
        <Loading />
    ) : (
        <>
            <AnimatePresence>
                {error.hasError && (
                    <PopupInfo text={error.message} setError={setError} />
                )}
            </AnimatePresence>
        </>
    );
};

export default CheckoutInit;
